import React, { useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { DropdownButtonProps } from '../../../types/DesignSystem/DropdownButton';
import { FOCUS_RING, FOCUS_RING_INSET } from '../focusRing';
import { buttonVariantClass } from '../Button/Button';
import { BUTTON_INSET, CONTROL_GAP, CONTROL_HEIGHT } from '../controlSize';
import { ChevronDown } from '../Iconography/Icons';
import { useDismissable } from '../useDismissable';
import { handleListNavKey } from '../listKeyboardNav';
import { MOTION } from '../motion';
import { usePresence } from '../usePresence';
import { POPOVER_ROOM_PX, fixedAnchorStyle, preferUpward, samePosition, useAnchorUpdates } from '../useAnchorUpdates';
import { useControlSize } from '../DensityProvider/DensityProvider';
import { withClassName } from '../cn';
import { useMergedRef } from '../useMergedRef';

const DropdownButton = React.forwardRef<HTMLButtonElement, DropdownButtonProps>(function DropdownButton({
  label,
  options,
  onSelect,
  variant = 'primary',
  menuVariant = 'default',
  disabled = false,
  className = '',
  align = 'start',
  icon,
  showChevron = true,
  size: sizeProp,
  usePortal = true,
  menuMaxHeight,
  'data-testid': dataTestId,
  onClick: onClickProp,
  onKeyDown: onKeyDownProp,
  ...rest
}, forwardedRef) {
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);
  /* § 359 — `end` (or the older `right`) hangs the menu from the trigger's far edge. */
  const alignEnd = align === 'end' || align === 'right';
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /* § 384 — the caller's ref reaches the trigger too. */
  const setTriggerRef = useMergedRef<HTMLButtonElement>(triggerRef, forwardedRef);
  const menuRef = useRef<HTMLDivElement>(null);
  const uid = React.useId();
  /* § 384 — a caller's own `id` is the trigger's; the menu is labelled by it either way. */
  const triggerId = (rest.id as string | undefined) ?? `${uid}-trigger`;
  const menuId = `${uid}-menu`;
  /* § 242 — held for its exit (popup-rise-out), aria-hidden and inert while leaving;
     and it FLIPS: measured at open and on every scroll/resize while up, the menu
     opens above a trigger that sits near the bottom of the viewport. */
  const { isPresent, phase } = usePresence(isOpen, MOTION.popover.exitMs);
  const exiting = phase === 'exiting';
  const [openUp, setOpenUp] = useState(false);
  /* § 304 — the fixed placement of the portalled menu, aligned as the inline one was. */
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);
  const measure = useCallback(() => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const above = preferUpward(rect, POPOVER_ROOM_PX);
    setOpenUp(above);
    if (usePortal) {
      // § 334 — once the menu has rendered, its width slides it back inside the viewport and
      // its natural height caps it (and makes it scroll) only if it would run off-screen.
      const menuEl = menuRef.current;
      const next = fixedAnchorStyle(rect, {
        above,
        align: alignEnd ? 'end' : 'start',
        fit: true,
        // offsetWidth, not the rect: the entry animation scales the box, and a mid-animation
        // rect is narrower than the surface will be.
        floatingWidth: menuEl?.offsetWidth,
        floatingHeight: menuEl?.scrollHeight,
        anchorEl: containerRef.current,
      });
      setMenuStyle((prev) => (prev && samePosition(prev, next) ? prev : next));
    }
  }, [usePortal, alignEnd]);
  useAnchorUpdates(isPresent && !exiting, containerRef, measure, menuRef);
  /* § 334 — the first measure runs before the menu exists; measure again once it has mounted. */
  const menuMounted = isPresent && menuStyle !== null;
  React.useLayoutEffect(() => {
    if (menuMounted && usePortal) measure();
  }, [menuMounted, usePortal, measure]);

  /*
    § 304 — the APG menu-button keyboard. With the menu portalled it is no longer the
    next thing in the DOM after its trigger, so "Tab into the items" — the only
    keyboard route this control ever had — would have landed at the end of the body.
    So: a keyboard OPEN (Enter / Space, `event.detail === 0`; ArrowDown; ArrowUp for the
    last item) moves focus into the menu, arrows and type-ahead move within it, a
    keyboard PICK returns focus to the trigger, Escape and Tab close and return focus
    to the trigger. Items leave the tab order (`tabIndex={-1}`) — the menu is one
    stop, its trigger. A mouse user sees nothing different.
  */
  /* The frame a keyboard open waits for. A close cancels it: the menu stays mounted through its
     exit animation, so a frame that fired after an Escape pressed straight after ArrowDown found
     the closing menu's first item and focused it — and when that menu unmounted, focus fell to
     the page. */
  const focusFrame = useRef<number | null>(null);
  const cancelFocusFrame = () => {
    if (focusFrame.current !== null) cancelAnimationFrame(focusFrame.current);
    focusFrame.current = null;
  };
  const focusItem = (which: 'first' | 'last') => {
    cancelFocusFrame();
    focusFrame.current = requestAnimationFrame(() => {
      focusFrame.current = null;
      const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
      if (!items || items.length === 0) return;
      (which === 'first' ? items[0] : items[items.length - 1]).focus();
    });
  };
  const openMenu = (focus?: 'first' | 'last') => {
    measure();
    setIsOpen(true);
    if (focus) focusItem(focus);
  };
  const closeMenu = (returnFocus: boolean) => {
    cancelFocusFrame();
    setIsOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };
  const focusIsInMenu = () => !!menuRef.current?.contains(document.activeElement);

  /**
   * These now agree with `Button`'s variants. They used to run the ramp BACKWARDS.
   *
   * **What was wrong (2026-08-24).** `primary` rested on `blue-500` and darkened to
   * `blue-900` on hover, while `Button.primary` rests on `blue-900` and brightens to
   * `blue-500`. Both cannot be right, and `blue-900` is the one the design system
   * actually settled on: it is the resting active/selected surface in EIGHT places —
   * `Radio` and `Checkbox` checked fills, the `Tab` label and its indicator,
   * `Spinner`'s arc, `Status.generating`, `Badge.secondary`, and all three `Button`
   * variants. `blue-500` rests nowhere else. So this component was the outlier, not
   * `Button`, and flipping `Button` instead — which is what a "primaries darken on
   * hover" convention would suggest — would have desynchronised the primary button
   * from those eight surfaces to fix one.
   *
   * `tertiary` had the same fault in text form: `blue-500` where `Button.tertiary`
   * uses `blue-900`.
   *
   * **Disabled** now matches `Button`'s `disabledStyle` too. It was
   * `disabled:tw-bg-grey-450` with the inherited `tw-text-white` left on top —
   * white on `#b5bcc8` is 1.91:1. SC 1.4.3 exempts inactive controls so it was not a
   * violation, but it was not legible either, and `grey-200`/`grey-600` is what every
   * other disabled control in the system uses.
   *
   * `secondary` is deliberately NOT touched: it is a grey-outlined button where
   * `Button.secondary` is a blue-outlined one. That is a difference in shape rather
   * than a wrong ramp end, so it is a design call, not a defect. Flagged in
   * `okf/design-system/e2e.md` § 29.
   */
  /*
    § 350 (2026-09-29) — the table this docblock describes is GONE: the trigger resolves
    its colours through `Button`'s own `buttonVariantClass`, so the two cannot drift
    again. It had drifted twice more after the fixes above — `secondary` hovered to the
    neutral row ground with an `edge-strong` border where every secondary Button takes
    the accent tint and `accent-hover`, and `secondary`/`tertiary` dimmed their disabled
    label to `ink-disabled` where Button's read `ink-tertiary` — so a button changed its
    look the moment it gained a menu. Disabled is decided by the prop rather than the
    `disabled:` variant, exactly as Button does it; the rendered states are Button's.
  */

  useDismissable({
    isOpen,
    /* Escape hands focus back to the trigger when it was in the menu — the menu is
       about to unmount, and focus must not fall to the body. An outside click leaves
       focus where the click put it. */
    onDismiss: (reason) => closeMenu(reason === 'escape' && focusIsInMenu()),
    /* § 304 — the portalled menu is not a DOM descendant of the trigger's box. */
    isInside: (target) => !!containerRef.current?.contains(target) || !!menuRef.current?.contains(target),
  });

  /*
    Row density and inset are `Menu`'s, not this component's own invention — the design
    system had two popover menus disagreeing about both. `Menu` and `Dropdown`'s listbox
    both inset their rows `px-3`; this one was the lone `px-4`, so the same list of labels
    started 4px further in depending on which component drew it. The vertical pair is
    `Menu`'s exactly (`py-2` / `py-1`), so "compact" means one thing across the system.
  */
  const itemPad = menuVariant === 'compact' ? 'tw-py-1' : 'tw-py-2';

  const handleSelect = (optionId: string | number, viaKeyboard: boolean) => {
    onSelect?.(optionId);
    closeMenu(viaKeyboard && focusIsInMenu());
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      closeMenu(true);
      return;
    }
    handleListNavKey(event, {
      container: menuRef.current,
      itemSelector: '[role="menuitem"]',
      orientation: 'vertical',
      typeAhead: true,
      /* § 403 — the arrows reach a disabled item too; only the pick is refused. */
      skipDisabled: false,
    });
  };

  const menu = (
    <div
      ref={menuRef}
      id={menuId}
      role="menu"
      aria-labelledby={triggerId}
      aria-hidden={exiting || undefined}
      onKeyDown={handleMenuKeyDown}
      /* § 304 — the marker for Dialog's outside-click guard and the modal Tab cycle;
         the fixed place from the shared engine. */
      data-dropdown-portal={usePortal ? uid : undefined}
      /* § 215 — a long list scrolls inside the menu instead of running off the viewport. */
      style={{
        ...(usePortal ? menuStyle ?? {} : {}),
        /* § 334 — a caller's cap still applies, but never beyond the room on screen. */
        ...(menuMaxHeight ? {
          maxHeight: usePortal ? `min(${menuMaxHeight}, var(--ds-popover-room, ${menuMaxHeight}))` : menuMaxHeight,
          overflowY: 'auto' as const,
        } : {}),
      }}
      /* `ds-md` (§ 219, was ds-lg): the POPOVER tier — Dropdown's listbox and
         Menu both sit there, and § 42 reserves ds-lg for overlays (Dialog,
         SidePanel, Toast). One floating-menu elevation, three components.
         The origin follows the flip (§ 242): the menu grows out of the trigger
         whichever side it opens on, and folds back into it on exit. Inline
         (`usePortal={false}`) keeps the § 242 geometry and its `tw-z-overlay`. */
      className={`${
        usePortal
          ? (openUp ? 'tw-origin-bottom' : 'tw-origin-top')
          : `tw-absolute ${openUp ? 'tw-bottom-full tw-mb-1 tw-origin-bottom' : 'tw-top-full tw-mt-1 tw-origin-top'} tw-z-overlay ${alignEnd ? 'tw-right-0' : 'tw-left-0'}`
      } tw-bg-surface-raised tw-rounded-srf tw-border tw-border-edge tw-shadow-ds-md tw-min-w-max ${menuMaxHeight ? 'tw-overscroll-contain ' : ''}${
        exiting ? `${MOTION.popover.exit} tw-pointer-events-none` : MOTION.popover.enter
      }`}
    >
      {options.map((option) => (
        <button
          type="button"
          key={option.id}
          role="menuitem"
          tabIndex={-1}
          data-testid={dataTestId ? `${dataTestId}-option-${option.id}` : undefined}
          /* § 403 — a disabled item is FOCUSABLE and refuses the pick: `aria-disabled`, not
             the native attribute, which took it out of the arrow keys' reach, so a keyboard
             or screen-reader user could not learn it was there (the APG menu rule; Menu has
             the same). Opening still lands on the first ENABLED item (`focusItem`). */
          onClick={(event) => { if (!option.disabled) handleSelect(option.id, event.detail === 0); }}
          aria-disabled={option.disabled || undefined}
          className={`tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 ${itemPad} tw-text-left tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift first:tw-rounded-t-srf last:tw-rounded-b-srf ${FOCUS_RING_INSET} ${
            option.disabled
              /* § 228 — one recede, not opacity × ink-disabled (see Menu). § 403 — GrayText
                 under forced colors, which an `aria-disabled` row does not get from the browser. */
              ? 'tw-cursor-not-allowed tw-text-ink-disabled forced-colors:tw-text-[color:GrayText]'
              /* Hover is NEUTRAL, selected is brand (§ 49/§ 51). These rows
                 hovered blue-100 — the selection tint — in a menu of actions
                 where nothing is ever selected. The ghost BUTTON's blue-100
                 wash above is different grammar (§ 33, button fills) and stays. */
              : 'hover:tw-bg-surface-hover-strong active:tw-bg-tone-neutral-tint tw-text-ink tw-cursor-pointer'
          }`}
        >
          {option.icon && <span className="tw-flex-shrink-0">{option.icon}</span>}
          <span className="tw-text-body-primary">{option.label}</span>
        </button>
      ))}
    </div>
  );

  return (
    <div ref={containerRef} className={withClassName('tw-relative tw-inline-block', className)}>
      <button
        /* § 384 — rest first (the Button.tsx rule); the menu-button wiring below wins. */
        {...rest}
        ref={setTriggerRef}
        id={triggerId}
        type="button"
        onClick={(event) => {
          /* A caller's handler runs first; `preventDefault()` in it keeps the menu shut. */
          onClickProp?.(event);
          if (event.defaultPrevented) return;
          if (isOpen) {
            closeMenu(false);
            return;
          }
          /* `detail === 0` is a keyboard activation (Enter / Space): the menu opens
             WITH focus on its first item, the APG menu-button arrangement. */
          openMenu(event.detail === 0 ? 'first' : undefined);
        }}
        onKeyDown={(event) => {
          /* A caller's handler runs first, as `onClick`'s does (and the anchored Menu's
             trigger's); `preventDefault()` in it keeps an arrow from opening the menu. */
          onKeyDownProp?.(event);
          if (disabled || event.defaultPrevented) return;
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!isOpen) openMenu(event.key === 'ArrowDown' ? 'first' : 'last');
            else focusItem(event.key === 'ArrowDown' ? 'first' : 'last');
          }
        }}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        data-testid={dataTestId}
        /* Shares the control grid with `Button` — this trigger computed 36px from
           `py-2` while `Button` declared 32, so the design system's two buttons
           disagreed with each other. See `controlSize.ts`. */
        /* § 229 — and the § 219 press: Button and IconButton scale 0.98 under the
           pointer and settle on `ease-swift`; this was the one button-shaped control
           that neither pressed nor eased, ten pixels from two that do. `transition-all`
           because transform is what the press animates (Button's exact reasoning); a
           disabled button receives no `:active`, so the guard is the browser's. */
        className={`tw-rounded-ctl tw-text-title tw-select-none tw-transition-all tw-duration-fast tw-ease-swift motion-safe:active:tw-scale-[0.98] tw-flex tw-items-center ${CONTROL_HEIGHT[size]} ${BUTTON_INSET[size]} ${CONTROL_GAP[size]} ${FOCUS_RING} ${
          buttonVariantClass(variant, { disabled })
        }${disabled ? '' : ' tw-cursor-pointer'}`}
      >
        {icon && <span className="tw-flex-shrink-0">{icon}</span>}
        <span>{label}</span>
        {showChevron && (
          /* `ChevronDown`, not the old inline `M19 14l-7 7m0 0l-7-7m7 7V3` — that was a
             long down-ARROW with a shaft, where `Dropdown` next to it drew a chevron.
             `currentColor` is load-bearing here: this button's text is white on
             `primary`, `grey-900` on `secondary` and `blue-900` on `tertiary`, so the
             chevron has to follow the label rather than name a colour. */
          <ChevronDown
            aria-hidden="true"
            className={`tw-flex-shrink-0 motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift ${
              isOpen ? 'tw-rotate-180' : ''
            }`}
          />
        )}
      </button>

      {isPresent && (!usePortal || menuStyle !== null) && (usePortal ? createPortal(menu, document.body) : menu)}
    </div>
  );
});

export default DropdownButton;
